import { AnalyticsModel } from '@models/analytics.model';
import type { WorkflowModel } from '@models/workflow.model';
import {
  type CreateJobDto,
  createJobSchema,
  type EtlJob,
  etlJobSchema,
  type Job,
  type JobExecution,
  JobExecutionStatus,
  jobExecutionSchema,
  type UpdateJobDto,
  updateJobSchema
} from '@stratumn/jobs';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { newError } from '@/services/errors/errors';
import { ENV } from '@/utils/constants';
import { parseWithZod } from '@/utils/parseZodSchema';

import BaseStore from './base/base.store';
import type RootStore from './root.store';

export class AnalyticsStore extends BaseStore<AnalyticsModel> {
  fetchingAnalyticsPages: Map<string, Set<number>> = new Map();
  fetchingAnalytics: Set<string> = new Set();
  private paginatedJobExecutions: Map<
    AnalyticsModel['id'],
    Map<number, JobExecution[]>
  > = new Map();
  pollingIntervals: Map<string, NodeJS.Timeout> = new Map();
  constructor(rootStore: RootStore) {
    super(rootStore, AnalyticsModel, 'analytics');
    this.store_ready = true;

    makeObservable(this, {
      fetchingAnalyticsPages: observable,
      fetchingAnalytics: observable,
      fetchAndParseAnalytics: action
    });
  }
  /* --------------------------------- Helpers -------------------------------- */

  public async fetchAndParseAnalytics(
    etl_id: AnalyticsModel['id'],
    workflow_published_id: string
  ): Promise<EtlJob | undefined> {
    if (this.fetchingAnalytics.has(workflow_published_id)) {
      return;
    }

    this.fetchingAnalytics.add(workflow_published_id);
    const fetchedSingleEtl = await this.httpWrapper
      .get<Job>(`/etl/${etl_id}`)
      .catch(error => {
        newError('ETL-N0i0W', error);
        this.fetchingAnalytics.delete(workflow_published_id);
        return;
      });

    runInAction(() => {
      this.fetchingAnalytics.delete(workflow_published_id);
    });

    if (!fetchedSingleEtl || Object.keys(fetchedSingleEtl).length === 0) {
      newError('ETL-MWwSj', `ETL with ID  ${etl_id} not found`);
      return;
    }

    const parsedAnalytics = this.parseFetchedAnalytics(fetchedSingleEtl);
    if (!parsedAnalytics) {
      return;
    }

    // Updating job executions
    if (
      parsedAnalytics.jobExecutions &&
      parsedAnalytics.jobExecutions.length > 0
    ) {
      const analyticsToUpdate = this.analyticsToPoll.find(a => a.id === etl_id);
      runInAction(() => {
        parsedAnalytics.jobExecutions &&
          analyticsToUpdate?.setJobExecutions(parsedAnalytics.jobExecutions);
      });
    }

    return parsedAnalytics;
  }

  public parseFetchedAnalytics(analytics: unknown): EtlJob | undefined {
    const parsedAnalyticsData = etlJobSchema.safeParse(analytics);

    if (!parsedAnalyticsData.success) {
      newError('ETL-oVUUz', parsedAnalyticsData.error);
      return;
    }
    return parsedAnalyticsData.data;
  }

  public loadEtlJob(analytics: EtlJob): AnalyticsModel {
    const newAnalytics = new AnalyticsModel(this, analytics);

    this.set(analytics.id, newAnalytics);

    return newAnalytics;
  }

  getJobById(id: Maybe<AnalyticsModel['id']>): Maybe<AnalyticsModel> | Error {
    if (!id) {
      return;
    }
    const job: Maybe<AnalyticsModel> = this.get(id);

    if (!job) {
      newError(
        'ETL-usKgP',
        `job with id ${id} not found`,
        ['local', 'staging'].includes(ENV),
        {
          errorType: 'warning'
        }
      );
      return;
    }

    return job;
  }

  public async createEtlJob(job: CreateJobDto) {
    try {
      const parsedData = parseWithZod(createJobSchema, job, 'ETL-Lm9nc');

      if (!parsedData) {
        throw newError('DND-lyVp4', 'Invalid input : Failed Zod validation.');
      }

      const result = await this.httpWrapper.post<EtlJob>('/', parsedData);

      if (!result?.id) {
        throw newError(
          'ETL-upnOp',
          `Failed to create ETL job. Invalid response: ${JSON.stringify(result)}`
        );
      }

      return this.loadEtlJob(result);
    } catch (error) {
      newError('ETL-MIkoD', error, true);
      return null;
    }
  }

  public async updateEtlJob(id: AnalyticsModel['id'], job: UpdateJobDto) {
    const parsedData = parseWithZod(updateJobSchema, job, 'ETL-AUxGX');
    if (!parsedData) {
      return;
    }
    const etlJob: Maybe<AnalyticsModel> =
      await this.httpWrapper.post<AnalyticsModel>(`/${id}`, parsedData);
    const etlStore = this.get(id);
    if (etlJob && etlStore) {
      runInAction(() => {
        Object.assign(etlStore, job);
      });
    }

    return etlJob;
  }

  public async triggerEtlJob(
    id: AnalyticsModel['id'],
    workflow: WorkflowModel
  ): Promise<JobExecution | undefined> {
    const jobExecution = await this.httpWrapper.post<JobExecution>(
      `/${id}/trigger`,
      {}
    );
    const parseJobExecution = jobExecutionSchema.safeParse(jobExecution);
    if (!parseJobExecution.success) {
      newError('ETL-6Ka4t', parseJobExecution.error);
      return;
    }
    workflow.analyticPageStore.setTotalNumberOfItemsInDB(
      workflow.analyticPageStore.getTotalNumberOfItemsInDB() + 1
    );

    const etlStore = this.get(id);
    if (etlStore) {
      runInAction(() => {
        etlStore.jobExecutions = [
          parseJobExecution.data,
          ...etlStore.jobExecutions
        ];
      });
    }
    const currentJobExecutionPage = this.getJobExecutionPageItems(id, 1);
    const newJobExecutionList = [
      parseJobExecution.data,
      ...currentJobExecutionPage
    ];
    this.setJobExecutionPage(id, 1, newJobExecutionList);
    return jobExecution;
  }

  get analyticsToPoll(): AnalyticsModel[] {
    return this.toArray().filter(analytics => analytics.shouldBePolled);
  }

  get hasRunningJobs() {
    return this.analyticsToPoll.some(a =>
      a.jobExecutions.some(
        jobExecution => jobExecution.status === JobExecutionStatus.RUNNING
      )
    );
  }

  public startPolling(workflow: WorkflowModel) {
    if (!workflow || this.analyticsToPoll.length < 1) {
      return;
    }

    this.analyticsToPoll.forEach(analytic => {
      if (analytic.areAllJobExecutionsFinished) {
        return;
      }

      if (this.pollingIntervals.has(analytic.id)) {
        return;
      }

      const interval = setInterval(() => {
        if (!workflow?.published_id) {
          return;
        }

        void this.fetchAndParseAnalytics(analytic.id, workflow.published_id)
          .then(updatedAnalytics => {
            if (!updatedAnalytics) {
              return;
            }
            if (analytic.areAllJobExecutionsFinished) {
              clearInterval(interval);
              return;
            }
          })
          .catch(error => newError('ETL-OlKv', error));
      }, 5000);

      this.pollingIntervals.set(analytic.id, interval);
    });
  }

  public startSinglePolling(
    analytics: AnalyticsModel,
    workflow: WorkflowModel
  ) {
    if (!(analytics?.shouldBePolled && workflow?.published_id)) {
      return;
    }

    if (this.pollingIntervals.has(analytics.id)) {
      return;
    }

    const interval = setInterval(() => {
      if (!workflow?.published_id) {
        return;
      }

      void this.fetchAndParseAnalytics(analytics.id, workflow.published_id)
        .then(updatedAnalytics => {
          if (!updatedAnalytics || analytics.areAllJobExecutionsFinished) {
            this.stopPolling(analytics.id);
          }
        })
        .catch(error => newError('ETL-OlKv', error));
    }, 5000);

    this.pollingIntervals.set(analytics.id, interval);
  }
  public stopPolling(analyticId: AnalyticsModel['id']) {
    if (this.pollingIntervals.has(analyticId)) {
      clearInterval(this.pollingIntervals.get(analyticId));
      this.pollingIntervals.delete(analyticId);
    }
  }

  public stopAllPolling() {
    this.pollingIntervals.forEach(interval => clearInterval(interval));
    this.pollingIntervals.clear();
  }

  /* --------------------------------- Pagination -------------------------------- */

  public isAnalyticsPageFetching(workflow_id: string, page: number): boolean {
    return this.fetchingAnalyticsPages.get(workflow_id)?.has(page) ?? false;
  }
  public setAnalyticsPageFetching(
    workflow_id: string,
    page: number,
    isFetching: boolean
  ): void {
    if (!this.fetchingAnalyticsPages.has(workflow_id)) {
      this.fetchingAnalyticsPages.set(workflow_id, new Set());
    }
    const pages = this.fetchingAnalyticsPages.get(workflow_id);
    if (!pages) {
      return;
    }

    isFetching ? pages.add(page) : pages.delete(page);
  }

  setJobExecutionPage(
    analyticsId: string,
    page: number,
    jobExecutions: JobExecution[]
  ): void {
    if (!this.paginatedJobExecutions.has(analyticsId)) {
      this.paginatedJobExecutions.set(analyticsId, new Map());
    }
    const pages = this.paginatedJobExecutions.get(analyticsId);
    pages?.set(page, jobExecutions);
  }

  getJobExecutionPageItems(analyticsId: string, page: number): JobExecution[] {
    const pages = this.paginatedJobExecutions.get(analyticsId);
    if (!pages) {
      return [];
    }
    return pages.get(page) || [];
  }
}
